import { useQuery } from '@apollo/client/react';
import { useRouter } from 'next/router';
import { useEffect } from 'react';

import { CandidateDocument } from '@pflegia/data-access/candidate.generated';
import { useMultipleVariantData } from '@pflegia/feature-flags/hooks/use-multiple-variant-data';
import * as SkipPasswordSetupFeatureFlag from '@pflegia/feature-flags/multiple-variant/skip-password-setup';
import { LoginMethod } from '@pflegia/types/common/enums/last-login-method.enum';
import { useAuth } from '@pflegia/ui/contexts/auth.context';
import {
  CANDIDATE_SECOND_STEP_REGISTRATION,
  IMPRINT_AND_PRIVACY,
  JOB_BOARD,
  MAINTENANCE,
  SET_PASSWORD,
  TERMS_AND_CONDITIONS,
} from '@pflegia/ui/pflegia/constants/routes';

export const useVerifyPasswordNotSet = () => {
  const { asPath, pathname, push } = useRouter();
  const authenticated = useAuth();
  const { isTest: isSkipPasswordSetupTest, isLoading: isSkipPasswordSetupLoading } = useMultipleVariantData(
    SkipPasswordSetupFeatureFlag.Name,
  );

  const { data } = useQuery(CandidateDocument, {
    fetchPolicy: 'cache-and-network',
    skip:
      !authenticated ||
      isSkipPasswordSetupLoading ||
      isSkipPasswordSetupTest ||
      [CANDIDATE_SECOND_STEP_REGISTRATION, MAINTENANCE, TERMS_AND_CONDITIONS, IMPRINT_AND_PRIVACY, SET_PASSWORD].includes(pathname),
  });

  useEffect(() => {
    // Until the flag resolves the test group is indistinguishable from the control group, and redirecting on that
    // guess bounces test-group candidates off whatever page they were on.
    if (isSkipPasswordSetupLoading) return;

    if (isSkipPasswordSetupTest && asPath.startsWith(CANDIDATE_SECOND_STEP_REGISTRATION)) {
      // Skip-password test group never sets a password, so bounce them off the second step to the job board.
      push({ pathname: JOB_BOARD });
      return;
    }

    if (
      !data ||
      (!data.candidate.emailConfirmedAt && data.candidate.hasDirectJobBoardAccess) ||
      data.candidate.hasPassword ||
      data.candidate.hasLinkedSocialMedias ||
      data.candidate.lastLoginMethod === LoginMethod.SmsOtp ||
      asPath.startsWith(CANDIDATE_SECOND_STEP_REGISTRATION) ||
      asPath.startsWith(SET_PASSWORD)
    )
      return;

    push({ pathname: CANDIDATE_SECOND_STEP_REGISTRATION });
  }, [asPath, data, isSkipPasswordSetupLoading, isSkipPasswordSetupTest, push]);
};
